import React, { useEffect, useState } from "react";
import Header from "../../../components/Header";
import { Upload, trips } from "../../../common/svg";
import { useForm } from "react-hook-form";
import UploadDocumentsModal from "../../Forms/Upload/Upload";
import { yupResolver } from "@hookform/resolvers/yup";
import AdminCointainer from "../../Admin";
import FormTitle from "../../../components/FormTitle";
import CustomDropDown from "../../../components/Dropdown/CustomDropDown";
import Input from "../../../components/FormInputs";
import DatePicker from "../../../components/Datepicker";
import CustomTabs from "../../../components/Tab";
import { TripFormTabs } from "../../../common/tabs";
import Cargo from "./Cargo";
import { validationSchema } from "../../../service/validation/trip";
import { TripBookingData } from "../../../service/interfaces/Forms";
import { ApiService } from "../../../service/api.service";
import { AUTH_URL, OPERTIONS_URL } from "../../../common/common";
import { useNavigate, useParams } from "react-router-dom";
import SecureImage from "../../../components/BucketImage/Image";
import DynamicModalDropDown, {
  ColumnConfig,
} from "../../../components/Dropdown/DynamicModalDropDown";
import RouteSelection from "./Components/RoutesSelect";
import DocumentComponent from "../Client/Components/document";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

const Newtrip = () => {
  const { bookingID } = useParams();
  const apiService = new ApiService();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Cargo Details");
  const entityId = localStorage.getItem("entity_id") || "";
  const [bookingData, setBookingData] = useState<
    { label: string; value: string }[]
  >([]);
  const [employeeData, setEmployeeData] = useState<
    { label: string; value: string }[]
  >([]);
  const [uploadedImages, setUploadedImages] = useState<
    { url: string; name: string }[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [routesDetails, setRoutesDetails] = useState<any[]>([]);
  const [files, setFiles] = useState<File[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [cargoDetails, setCargoDetails] = useState<any[]>([]);

  // Determine if we have a bookingID from params
  const hasBookingIdFromParams = Boolean(bookingID);

  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TripBookingData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      tripDetail: {
        tripName: "",
        tripNumber: "",
        startingDateTime: new Date(),
        endingDateTime: new Date(),
        lrNumber: "",
        eWayBill: "",
        managingBy: "",
      },
      // Only set bookingId if it exists in params
      bookingId: hasBookingIdFromParams ? bookingID : "",
      cargoDetails: [],
    },
  });

  const bookingId = watch("bookingId", "");

  const dispatch = useDispatch();
  const tripDetail = useSelector((state: RootState) => state.tripBooking.tripDetail);

  const onSubmit = async (data: TripBookingData) => {
    try {
      console.log("Form Data", data, selectedRouteIds, files, uploadedFiles);
      const formData = new FormData();

      const { tripDetail, bookingId, cargoDetails } = data;

      // Filter tripDetail object
      const filteredTripDetail = Object.fromEntries(
        Object.entries(tripDetail || {}).filter(([_, value]) =>
          value !== null && value !== undefined && value !== ""
        )
      );

      if (Object.keys(filteredTripDetail).length > 0) {
        formData.append("tripDetail", JSON.stringify(filteredTripDetail));
      }

      // Only append bookingId if it exists and is not empty
      if (bookingId && bookingId.trim() !== "") {
        formData.append("bookingId", bookingId);
      }

      // Filter and append cargoDetails
      const filteredCargoDetails = (cargoDetails || [])
        .map(item =>
          Object.fromEntries(
            Object.entries(item).filter(([_, value]) =>
              value !== null && value !== undefined && value !== ""
            )
          )
        )
        .filter(item => Object.keys(item).length > 0);

      if (filteredCargoDetails.length > 0) {
        formData.append("cargoDetails", JSON.stringify(filteredCargoDetails));
      }

      // Add selected route details to payload
      if (selectedRouteIds.length > 0) {
        const routeDetails = selectedRouteIds.map(id => ({ tripRouteId: id }));
        formData.append("routeDetails", JSON.stringify(routeDetails));
      }

      const existingDocuments = uploadedFiles
        ?.filter(file => file.fileId)
        .map(file => ({
          fileId: file.fileId,
          fileName: file.fileName,
          s3BucketName: file.s3BucketName,
          s3ObjectKey: file.s3ObjectKey,
        }));

      if (existingDocuments && existingDocuments.length > 0) {
        formData.append("contactFiles", JSON.stringify(existingDocuments));
      }

      // New Files
      if (files && files.length > 0) {
        files.forEach(file => {
          formData.append("files", file);
        });
      }

      // Submit to API
      const response: any = await apiService.postApi(
        `${OPERTIONS_URL}/trip/store-route`,
        formData,
      );

      if (response?.status?.toString() === "200") {
        navigate("/trip", { replace: true });
      } else {
        console.error("Trip creation failed", response?.message);
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request was canceled");
        return;
      }

      const errorMessage = error.message || "Error submitting trip form";
      console.error("Trip submission error:", errorMessage);
    }
  };

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const getBookingData = async () => {
    const response: any = await apiService.getApi(
      `${OPERTIONS_URL}/booking/get-all-route?entityId=${entityId}`
    );
    if (response.status === "200") {
      let opData = response.body.data.map((item: any) => {
        return { label: item.bookingName, value: item.bookingId };
      });
      setBookingData(opData);
    }
  };

  const getRouteData = async (bookingIdToFetch: string) => {
    if (!bookingIdToFetch || bookingIdToFetch.trim() === "") {
      console.log("No booking ID provided for route data fetch");
      setRoutesDetails([]);
      return;
    }

    try {
      const response: any = await apiService.getApi(
        `${OPERTIONS_URL}/route/details-route?bookingId=${bookingIdToFetch}`
      );
      if (response.status === "200") {
        console.log("Route Data", response.body);
        setRoutesDetails(response.body);
      } else {
        console.log("Route Data Error", response);
        setRoutesDetails([]);
      }
    } catch (error) {
      console.error("Error fetching route data:", error);
      setRoutesDetails([]);
    }
  };

  const getEmployeeData = async (employeeId: string) => {
    // Validate employeeId before making API call
    if (!employeeId || employeeId === 'undefined' || employeeId === 'null') {
      console.warn('Invalid employee ID provided:', employeeId);
      return;
    }

    try {
      const response: any = await apiService.getApi(
        `${AUTH_URL}/employee/get-one-route?employeeId=${employeeId}`
      );

      if (response.status === "200" && response.body?.employeeDetails) {
        const employee = response.body.employeeDetails;
        const formattedExecutive = {
          value: employeeId,
          label: `${employee.firstName} ${employee.lastName || ""}`,
          emailId: employee.emailId,
          phoneNumber: employee.mobileNumber,
        };
        setEmployeeData([formattedExecutive]);
        setValue("tripDetail.managingBy", employeeId);
      } else {
        setEmployeeData([]);
        setValue("tripDetail.managingBy", "");
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      await getBookingData();
    };
    fetchData();
  }, []);

  // Fetch route data when bookingId changes (from params or form selection)
  useEffect(() => {
    if (bookingId && bookingId.trim() !== "") {
      getRouteData(bookingId);
    } else {
      setRoutesDetails([]);
      setSelectedRouteIds([]);
    }
  }, [bookingId]);

  // Set initial bookingId if coming from params
  useEffect(() => {
    if (hasBookingIdFromParams && bookingID) {
      setValue("bookingId", bookingID);
    }
  }, [hasBookingIdFromParams, bookingID, setValue]);

  const employeeColumns: ColumnConfig[] = [
    {
      key: "bookingName",
      header: "Booking Name",
      visible: true,
      formatter: (value, row) => value || "N/A",
    },
    {
      key: "bookingNumber",
      header: "Booking Number",
      visible: true,
    },
  ];

  const handleRouteSelect = (routeId: string | null) => {
    if (routeId) {
      setSelectedRouteIds(prev =>
        prev.includes(routeId)
          ? prev.filter(id => id !== routeId)
          : [...prev, routeId]
      );
    }
  };

  return (
    <div>
      <Header title="Add New Trip" icon={trips} showSearch={false}>
        <div onClick={toggleModal} className="cursor-pointer">
          <SecureImage fileKey={Upload} alt="header icon" width="30px" />
        </div>
      </Header>

      <AdminCointainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-white rounded-[20px] p-2 px-6">
            <FormTitle title="Trip Details" />
            <div className="">
              <div className="flex gap-2 flex-col md:flex-row mb-4">
                <DynamicModalDropDown
                  label="Booking Name"
                  name="bookingId"
                  endpoint="booking/get-all-route"
                  defaultValue={hasBookingIdFromParams ? bookingID : ""}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  columns={employeeColumns}
                  idField="bookingId"
                  labelField={(item) => `${item.bookingName || ""}`.trim()}
                  searchFields={["bookingName"]}
                  addNewPath="/booking/newbooking"
                  addNewLabel="Add new booking"
                  findByIdEndpoint={`${OPERTIONS_URL}/booking/details-route`}
                  onSelect={(employee) => {
                    getEmployeeData(employee.bookingCreatedBy);
                  }}
                  operationAPI={OPERTIONS_URL}
                  extraFetch={getEmployeeData}
                  extraFetchProp="bookingCreatedBy"
                  disabled={hasBookingIdFromParams} // Disable if bookingID comes from params
                />
                <Input
                  register={register}
                  name="tripDetail.tripName"
                  errors={errors}
                  label="Trip Name"
                  placeholder="Trip Name"
                  defaultValue={""}
                />
                <Input
                  register={register}
                  name="tripDetail.tripNumber"
                  errors={errors}
                  label="Trip Number"
                  placeholder="Trip Number"
                />
                <DatePicker
                  label="Start Date & Time"
                  onDateChange={(date: string) =>
                    setValue("tripDetail.startingDateTime", new Date(date))
                  }
                  register={register("tripDetail.startingDateTime")}
                  name="startingDateTime"
                  errors={errors}
                  disabled={false}
                  includeTime={true}
                  allowPastDates={false}
                />
              </div>
              <div className="flex gap-2 flex-col md:flex-row mb-4">
                <Input
                  register={register}
                  name="tripDetail.lrNumber"
                  errors={errors}
                  label="LR Number"
                  placeholder="Enter LR Number"
                  defaultValue={""}
                />
                <Input
                  register={register}
                  name="tripDetail.eWayBill"
                  errors={errors}
                  label="E way Bill"
                  placeholder="Enter E way bill Number"
                />
                <CustomDropDown
                  register={register}
                  name="tripDetail.managingBy"
                  errors={errors}
                  options={employeeData || []}
                  label="Managed By"
                  defaultValue={watch("tripDetail.managingBy")}
                />
                <DatePicker
                  label="End Date & Time"
                  onDateChange={(date: string) =>
                    setValue("tripDetail.endingDateTime", new Date(date))
                  }
                  register={register("tripDetail.endingDateTime")}
                  name="endingDateTime"
                  errors={errors}
                  disabled={false}
                  includeTime={true}
                  allowPastDates={false}
                />
              </div>
            </div>
          </div>
          <br />
          <CustomTabs tabs={TripFormTabs} onTabChange={setActiveTab} />
          <br />
          <div className="bg-white rounded-[20px] p-2 px-6">
            {activeTab === "Cargo Details" && (
              <Cargo
                cargoDetails={cargoDetails}
                setCargoDetails={setCargoDetails}
                register={register}
                setValue={setValue}
                clearErrors={clearErrors}
                errors={errors}
                watch={watch}
              />
            )}
            {activeTab === "Route" && (
              <RouteSelection
                setValue={setValue}
                watch={watch}
                register={register}
                routes={routesDetails}
                onRouteSelect={handleRouteSelect}
              />
            )}
            {activeTab === "Documents" && (
              <DocumentComponent
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                setUploadedImages={setUploadedImages}
                uploadedImages={uploadedImages}
                setFiles={setFiles}
                files={files}
              />
            )}
            {activeTab === "Address" && <div>Address</div>}
          </div>
          <div className="gap-4 flex items-center justify-end mt-4 mr-8 self-end">
            <button
              type="button"
              className="w-36 h-10 rounded-lg text-[#444FBD] border border-[#444FBD] hover:border-gray-400 transition duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              className="w-36 h-10 rounded-lg text-[#444FBD] border border-[#444FBD] hover:border-gray-400 transition duration-200"
            >
              Draft
            </button>
            <button
              type="submit"
              className="w-36 h-10 rounded-lg bg-[#444FBD] text-white transition duration-200"
              onClick={() => {
                console.log(errors);
              }}
            >
              Submit
            </button>
          </div>
        </form>
      </AdminCointainer>

      {/* Modal Section */}
      {isModalOpen && (
        <UploadDocumentsModal
          uploadedImages={uploadedImages}
          setUploadedImages={setUploadedImages}
          files={files}
          setFiles={setFiles}
          toggleModal={toggleModal}
        />
      )}
    </div>
  );
};

export default Newtrip;